import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import http from '@lib/http-client'
import { OpportunityStore } from './types'

export const useOpportunityStore = create<OpportunityStore>()(
  devtools(
    persist(
      (set, get) => ({
        categories: [],
        uniqueRules: [],

        findOpportunityCategoryById: (id: number) => {
          return get().categories.find(category => category.id === id)
        },

        fetchOpportunityCategories: async () => {
          try {
            const endpoint = '/opportunities/category'

            const response = await http.get(endpoint)

            if (!response || !Array.isArray(response)) {
              return false
            }

            set({ categories: response })

            return true
          } catch (error) {
            console.error('Error fetching opportunity categories:', error)
            return false
          }
        },

        fetchOpportunitiesUniqueRules: async () => {
          try {
            const endpoint = '/opportunities/uniques'

            const response = await http.get(endpoint)

            if (!response || !Array.isArray(response)) {
              return false
            }

            set({ uniqueRules: response })

            return true
          } catch (error) {
            console.error('Error fetching opportunity categories:', error)
            return false
          }
        },
        getOpportunitiesUniqueRulesByName: (ruleName: string) => {
          const { uniqueRules } = get()
          return uniqueRules?.find(rule => rule.rule_name === ruleName)
        },

        getOpportunitiesFromCategories: async (
          idCategory: number,
          page: number,
          pageSize: number
        ) => {
          try {
            const params = new URLSearchParams()
            params.append('page', page.toString())
            params.append('page_size', pageSize.toString())

            const endpoint = `/opportunities/category/${idCategory}?${params.toString()}`

            const response = await http.get(endpoint)

            return response
          } catch (error) {
            console.error('Error fetching opportunities:', error)
            return null
          }
        },

        getOpportunityDetailsFromId: async (opportunityId: string) => {
          try {
            const endpoint = `/opportunities/${opportunityId}`

            const response = await http.get(endpoint)

            return response
          } catch (error) {
            console.error('Error fetching opportunity details:', error)
            return null
          }
        },

        getOpportunitiesByUserId: async (userId: number) => {
          try {
            const endpoint = `/user/${userId}/opportunities`

            const response = await http.get(endpoint)

            if (!response || !Array.isArray(response)) {
              return null
            }

            return response
          } catch (error) {
            console.error('Error fetching opportunities by user ID:', error)
            return null
          }
        },

        getRuleQuestionsByUserId: async (ruleName: string, userId: number) => {
          try {
            const endpoint = `/opportunities/questions/${ruleName}/user/${userId}`

            const response = await http.get(endpoint)

            return response
          } catch (error) {
            console.error('Error fetching rule questions by user ID:', error)
            return null
          }
        },

        setRuleQuestionsByUserId: async (
          ruleName: string,
          userId: number,
          questions: any[]
        ) => {
          try {
            const endpoint = `/opportunities/questions/user/${userId}`
            const payload = {
              rule_name: ruleName,
              questions
            }

            const response = await http.post(endpoint, payload)

            return {
              success: response?.success,
              errorMessage: response?.error_message || null
            }
          } catch (error) {
            console.error('Error setting rule questions by user ID:', error)
            return { success: false, errorMessage: error.message }
          }
        },

        upsertOpportunityUniqueRule: async (
          rule_name: string,
          questions: any[]
        ) => {
          try {
            const endpoint = '/opportunities/questions'
            const payload = {
              rule_name,
              questions
            }
            const response = await http.post(endpoint, payload)

            return response.success
          } catch (error) {
            console.error('Error upserting opportunity unique rule:', error)
            return false
          }
        },

        activateOpportunityByUserId: async (
          userId: number,
          ruleName: string
        ) => {
          try {
            const endpoint = `/user/${userId}/opportunities/suggestion/active`
            const payload = { rule_name: ruleName }

            const response = await http.post(endpoint, payload)

            return response.success
          } catch (error) {
            console.error('Error activating opportunity by user ID:', error)
            return null
          }
        },

        deactivateOpportunityByUserId: async (
          userId: number,
          ruleName: string
        ) => {
          try {
            const endpoint = `/user/${userId}/opportunities/suggestion/deactivate`
            const payload = { rule_name: ruleName }

            const response = await http.post(endpoint, payload)

            return response.success
          } catch (error) {
            console.error('Error activating opportunity by user ID:', error)
            return null
          }
        },

        approveOpportunity: async (opportunityId: string) => {
          try {
            const endpoint = `/opportunities/${opportunityId}/approve`

            const response = await http.post(endpoint)
            return response
          } catch (error) {
            console.error('Error approving opportunity:', error)
            return null
          }
        },
        rejectOpportunity: async (opportunityId: string) => {
          try {
            const endpoint = `/opportunities/${opportunityId}/reject`

            const response = await http.post(endpoint)
            return response
          } catch (error) {
            console.error('Error rejecting opportunity:', error)
            return null
          }
        },

        clear: () => {
          set({ categories: [], uniqueRules: [] }, false, 'clear')
        }
      }),
      {
        name: 'opportunity-storage',
        storage: createJSONStorage(() => sessionStorage),
        partialize: state => ({
          categories: state.categories
        })
      }
    )
  )
)
